import { useCallback, useEffect, useRef, useState } from 'react';

export function usePresence(documentId, enabled, { onDocument, onAccess, onRevoked }) {
  const [status, setStatus] = useState('connecting'), [peers, setPeers] = useState([]);
  const handlers = useRef({}); handlers.current = { onDocument, onAccess, onRevoked };
  const connection = useRef(null), state = useRef({ cursor: null, selectedId: null }), timer = useRef(null), lastSent = useRef(0);
  const transmit = useCallback(() => {
    clearTimeout(timer.current); timer.current = null;
    if (connection.current?.readyState !== WebSocket.OPEN) return;
    lastSent.current = Date.now();
    connection.current.send(JSON.stringify({ type: 'presence', ...state.current }));
  }, []);
  const publish = useCallback(patch => {
    state.current = { ...state.current, ...patch };
    const delay = 50 - (Date.now() - lastSent.current);
    if (delay <= 0) transmit();
    else if (!timer.current) timer.current = setTimeout(transmit, delay);
  }, [transmit]);
  useEffect(() => {
    if (!enabled || !documentId) { setPeers([]); return; }
    let stopped = false, retry = null, attempts = 0, socket;
    function connect() {
      if (stopped) return;
      setStatus(attempts ? 'reconnecting' : 'connecting');
      const url = new URL('/api/collaboration', window.location.href);
      url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'; url.searchParams.set('documentId', documentId);
      const ws = new WebSocket(url); socket = ws; connection.current = ws;
      ws.onmessage = event => {
        if (stopped) return;
        let message; try { message = JSON.parse(event.data); } catch { return; }
        if (message.type === 'welcome') {
          attempts = 0; setStatus('online'); setPeers(message.peers);
          handlers.current.onAccess?.(message.role); handlers.current.onDocument?.(message.revision);
          transmit();
        } else if (message.type === 'peer') setPeers(old => [...old.filter(peer => peer.clientId !== message.peer.clientId), message.peer]);
        else if (message.type === 'leave') setPeers(old => old.filter(peer => peer.clientId !== message.clientId));
        else if (message.type === 'document') handlers.current.onDocument?.(message.revision);
        else if (message.type === 'access') handlers.current.onAccess?.(message.role);
        else if (message.type === 'revoked') { stopped = true; setPeers([]); setStatus('revoked'); handlers.current.onRevoked?.(message.message); }
      };
      ws.onclose = event => {
        if (connection.current === ws) connection.current = null;
        if (stopped) return;
        if (socket !== ws) return;
        setPeers([]); setStatus('offline');
        if (event.code === 4003) { stopped = true; setStatus('revoked'); handlers.current.onRevoked?.('访问权限已失效。'); return; }
        clearTimeout(retry); retry = setTimeout(connect, Math.min(15000, 1000 * 2 ** Math.min(attempts++, 4)));
      };
      ws.onerror = () => ws.close();
    }
    const online = () => { clearTimeout(retry); if (!stopped && (!socket || socket.readyState >= WebSocket.CLOSING)) connect(); };
    const visibility = () => { if (document.hidden) publish({ cursor: null }); else online(); };
    const blur = () => publish({ cursor: null });
    window.addEventListener('online', online); window.addEventListener('blur', blur); document.addEventListener('visibilitychange', visibility);
    connect();
    return () => {
      stopped = true; clearTimeout(retry); clearTimeout(timer.current); timer.current = null;
      socket?.close(); connection.current = null; state.current = { cursor: null, selectedId: null };
      window.removeEventListener('online', online); window.removeEventListener('blur', blur); document.removeEventListener('visibilitychange', visibility);
    };
  }, [documentId, enabled, publish, transmit]);
  return { status, peers, publish };
}
